// CSV parser for the Delivery view's "Import CSV" flow. Handles the
// RFC-4180 essentials (quoted fields with embedded commas / newlines,
// escaped quotes via "", CRLF, BOM strip) without pulling in a library.
//
// Returns parsed rows with the canonical keys the server expects:
//   name, description, boundedContext, priority
// plus a list of warnings and per-row errors.

export interface CsvParseRow {
  name: string;
  description: string;
  guid?: string;                      // optional stable feature id for folder-based spec import
  boundedContext?: string;
  priority?: 'mvp' | 'later';
  migrationUnit?: string;            // groups features into MUs at import time
  effort?: 1 | 2 | 3 | 5 | 8 | 13 | 21;
  value?: 'high' | 'med' | 'low';
  tags?: string;                      // raw comma/semicolon-separated, server splits
  // Phase 2 columns (scope-items.csv shape) — all optional
  scopeType?: 'bc' | 'aggregate' | 'feature';
  stream?: string;                    // top-level grouping above BC
  specPath?: string;
  specConfidence?: 'high' | 'med' | 'low' | 'n/a';
  justification?: string;
}

export interface CsvParseResult {
  rows: CsvParseRow[];
  warnings: string[];
  errors: Array<{ rowIndex: number; reason: string }>;
  rawRowCount: number;
}

const HEADER_SYNONYMS: Record<string, keyof CsvParseRow> = {
  name: 'name',
  title: 'name',
  feature: 'name',
  description: 'description',
  desc: 'description',
  details: 'description',
  scope_name: 'name',
  scopename: 'name',
  guid: 'guid',
  id: 'guid',
  uuid: 'guid',
  feature_id: 'guid',
  featureid: 'guid',
  scope_id: 'guid',
  scopeid: 'guid',
  bounded_context: 'boundedContext',
  boundedcontext: 'boundedContext',
  context: 'boundedContext',
  bc: 'boundedContext',
  priority: 'priority',
  prio: 'priority',
  migration_unit: 'migrationUnit',
  migrationunit: 'migrationUnit',
  mu: 'migrationUnit',
  unit: 'migrationUnit',
  effort: 'effort',
  pts: 'effort',
  points: 'effort',
  story_points: 'effort',
  storypoints: 'effort',
  build_complexity: 'effort',
  buildcomplexity: 'effort',
  complexity: 'effort',
  value: 'value',
  business_value: 'value',
  businessvalue: 'value',
  tags: 'tags',
  tag: 'tags',
  labels: 'tags',
  // Scope-items columns (post-2026-05-12)
  scope_type: 'scopeType',
  scopetype: 'scopeType',
  type: 'scopeType',
  stream: 'stream',
  portfolio: 'stream',
  spec_path: 'specPath',
  specpath: 'specPath',
  spec: 'specPath',
  spec_confidence: 'specConfidence',
  specconfidence: 'specConfidence',
  confidence: 'specConfidence',
  build_complexity_justification: 'justification',
  buildcomplexityjustification: 'justification',
  justification: 'justification',
  rationale: 'justification',
};

const MAX_DESCRIPTION = 2048;
const MAX_NAME = 120;
const VALID_EFFORTS = new Set([1, 2, 3, 5, 8, 13, 21]);
const VALID_VALUES = new Set(['high', 'med', 'low']);

export function parseCsv(text: string): CsvParseResult {
  const warnings: string[] = [];
  const errors: Array<{ rowIndex: number; reason: string }> = [];
  const out: CsvParseRow[] = [];

  if (!text) return { rows: out, warnings: ['Empty file.'], errors, rawRowCount: 0 };

  // BOM strip.
  const cleaned = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;

  // Detect delimiter from the first line (tab vs comma); CSV wins by default.
  const firstLineEnd = cleaned.search(/\r?\n/);
  const firstLine = firstLineEnd === -1 ? cleaned : cleaned.slice(0, firstLineEnd);
  const delimiter = firstLine.includes('\t') && !firstLine.includes(',') ? '\t' : ',';

  const records = splitRecords(cleaned, delimiter);
  if (records.length === 0) {
    return { rows: out, warnings: ['No rows found.'], errors, rawRowCount: 0 };
  }

  // First record = header.
  const header = records[0].map((h) => h.trim().toLowerCase().replace(/\s+/g, '_'));
  const colMap = new Map<number, keyof CsvParseRow>();
  const unknownCols: string[] = [];
  for (let i = 0; i < header.length; i++) {
    const key = header[i];
    if (!key) continue;
    const mapped = HEADER_SYNONYMS[key];
    if (mapped) colMap.set(i, mapped);
    else unknownCols.push(key);
  }
  if (!Array.from(colMap.values()).includes('name')) {
    return {
      rows: out,
      warnings,
      errors: [{ rowIndex: 0, reason: 'CSV must include a "name" column.' }],
      rawRowCount: records.length - 1,
    };
  }
  if (unknownCols.length > 0) {
    warnings.push(`Ignored ${unknownCols.length} unknown column(s): ${unknownCols.slice(0, 5).join(', ')}${unknownCols.length > 5 ? ', …' : ''}`);
  }

  for (let r = 1; r < records.length; r++) {
    const cols = records[r];
    if (cols.every((c) => !c || !c.trim())) continue; // skip empty rows

    const row: CsvParseRow = { name: '', description: '' };
    for (const [colIdx, fieldName] of colMap.entries()) {
      const raw = cols[colIdx] ?? '';
      if (fieldName === 'name') row.name = raw.trim();
      else if (fieldName === 'description') row.description = raw.trim();
      else if (fieldName === 'guid') {
        const g = raw.trim();
        if (g) row.guid = g.slice(0, 120);
      }
      else if (fieldName === 'boundedContext') row.boundedContext = raw.trim();
      else if (fieldName === 'priority') {
        const p = raw.trim().toLowerCase();
        if (p === 'mvp' || p === 'later') row.priority = p;
        else if (p && p !== 'tbd' && p !== 'n/a' && p !== 'na') {
          warnings.push(`Row ${r}: priority "${raw}" not recognized — defaulted to "later".`);
        }
        // tbd / n/a / empty are silent placeholders.
      } else if (fieldName === 'migrationUnit') {
        const mu = raw.trim().slice(0, 30);
        if (mu && mu.toLowerCase() !== 'n/a') row.migrationUnit = mu;
      } else if (fieldName === 'effort') {
        const trimmed = raw.trim();
        if (!trimmed) continue;
        const n = Number(trimmed);
        if (VALID_EFFORTS.has(n)) row.effort = n as CsvParseRow['effort'];
        else warnings.push(`Row ${r}: effort "${raw}" not a Fibonacci value (1,2,3,5,8,13,21) — left unset.`);
      } else if (fieldName === 'value') {
        const v = raw.trim().toLowerCase();
        if (!v) continue;
        if (VALID_VALUES.has(v)) row.value = v as CsvParseRow['value'];
        else warnings.push(`Row ${r}: value "${raw}" not high/med/low — left unset.`);
      } else if (fieldName === 'tags') {
        const t = raw.trim();
        if (t) row.tags = t;
      } else if (fieldName === 'scopeType') {
        const s = raw.trim().toLowerCase();
        if (s === 'bc' || s === 'aggregate' || s === 'feature') row.scopeType = s;
        // Other values silently ignored.
      } else if (fieldName === 'stream') {
        const s = raw.trim();
        if (s) row.stream = s.slice(0, 60);
      } else if (fieldName === 'specPath') {
        const s = raw.trim();
        if (s) row.specPath = s.slice(0, 240);
      } else if (fieldName === 'specConfidence') {
        const c = raw.trim().toLowerCase();
        if (c === 'high') row.specConfidence = 'high';
        else if (c === 'medium' || c === 'med') row.specConfidence = 'med';
        else if (c === 'low') row.specConfidence = 'low';
        else if (c === 'n/a' || c === 'na') row.specConfidence = 'n/a';
        // Other values silently ignored.
      } else if (fieldName === 'justification') {
        const s = raw.trim();
        if (s) row.justification = s.slice(0, 2048);
      }
    }

    if (!row.name) {
      errors.push({ rowIndex: r, reason: 'Missing or empty name.' });
      continue;
    }
    if (row.name.length > MAX_NAME) {
      errors.push({ rowIndex: r, reason: `Name longer than ${MAX_NAME} chars.` });
      continue;
    }
    if (row.description.length > MAX_DESCRIPTION) {
      warnings.push(`Row ${r}: description truncated from ${row.description.length} to ${MAX_DESCRIPTION} chars.`);
      row.description = row.description.slice(0, MAX_DESCRIPTION);
    }

    out.push(row);
  }

  return { rows: out, warnings, errors, rawRowCount: records.length - 1 };
}

// Split CSV text into records (rows of fields), handling quoted fields.
function splitRecords(text: string, delimiter: string): string[][] {
  const records: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === delimiter) {
      row.push(field);
      field = '';
      continue;
    }
    if (ch === '\r') {
      // CRLF — consume the following \n.
      if (text[i + 1] === '\n') i++;
      row.push(field);
      records.push(row);
      field = '';
      row = [];
      continue;
    }
    if (ch === '\n') {
      row.push(field);
      records.push(row);
      field = '';
      row = [];
      continue;
    }
    field += ch;
  }

  // Final field / row if file doesn't end with a newline.
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    records.push(row);
  }

  return records;
}
